import { mapState } from 'vuex';
import moment from 'moment';
import { getTimeArrayFromRangeKey } from './pickerParseRangeValues';
import allowedTimeRangeMixins from './allowedTimeRangeMixins';
import { VContainer } from 'vuetify/lib';
export default {
  name: 'CoreDateTimePickerFlat',
  components: {
    VContainer
  },
  mixins: [allowedTimeRangeMixins],
  props: {
    from: {
      type: Number,
      default: null,
      required: false
    },
    to: {
      type: Number,
      default: null,
      required: false
    },
    allowNull: {
      type: Boolean,
      default: false,
      required: false
    },
    dense: {
      type: Boolean,
      default: false,
      required: false
    },
    disabled: {
      type: Boolean,
      default: false,
      required: false
    },
    isRange: {
      type: Boolean,
      default: false,
      required: false
    },
    initialRange: {
      type: String,
      default: 'now-10m'
    },
    dateOnly: {
      type: Boolean,
      default: false,
      required: false
    },
    futureRange: {
      type: Boolean,
      default: false,
      required: false
    },
    defaultExpand: {
      type: Boolean,
      default: false,
      required: false
    },
    hideBorderOnCollapse: {
      type: Boolean,
      default: false,
      required: false
    },
    alternateInitialText: {
      type: String,
      default: '',
      required: false
    },
    disableQuickRanges: {
      type: Boolean,
      default: false,
      required: false
    },
    container: {
      type: String,
      default: 'v-container',
      required: false
    },
    hideApply: {
      type: Boolean,
      default: false,
      required: false
    },
    closeOnSelect: {
      type: Boolean,
      default: true,
      required: false
    }
  },
  data: function () {
    return {
      expanded: [],
      menu: false,
      hideAlternateText: false,
      startDate: null,
      endDate: null,
      isDatePickerFormValid: true,
      inputValidationRules: {
        validDate: value => this.validateInputDate(value) || this.$i18n_t('Core.DateTimePicker.errorHintInvalidDate'),
        validRangeStart: value => this.validateInputRange(value, undefined) || this.$i18n_t('Core.DateTimePicker.errorHintStartAfterEnd'),
        validRangeEnd: value => this.validateInputRange(undefined, value) || this.$i18n_t('Core.DateTimePicker.errorHintEndBeforeStart')
      }
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color']),
    mathOp() {
      return this.futureRange ? '+' : '-';
    },
    expansionPanelsClass() {
      return this.hideBorderOnCollapse ? '' : 'kpi-card-text-01';
    },
    displayText() {
      if (this.alternateInitialText !== '' && !this.hideAlternateText) {
        return this.alternateInitialText;
      }
      return `${this.getLocaleDateFromUnix(this.startDate)} ${this.isRange ? this.$i18n_t('Core.DateTimePicker.headerTextJoinder') : ''} ${this.isRange ? this.getLocaleDateFromUnix(this.endDate) : ''}`;
    }
  },
  watch: {
    initialRange() {
      this.startDate = parseInt(this.from, 10) || (this.allowNull ? null : getTimeArrayFromRangeKey(this.initialRange)[0]);
      this.endDate = parseInt(this.to, 10) || (this.allowNull ? null : getTimeArrayFromRangeKey(this.initialRange)[1]);
    },
    from() {
      this.startDate = parseInt(this.from, 10) || (this.allowNull ? null : getTimeArrayFromRangeKey(this.initialRange)[0]);
    },
    to() {
      this.endDate = parseInt(this.to, 10) || (this.allowNull ? null : getTimeArrayFromRangeKey(this.initialRange)[1]);
    },
    defaultExpand() {
      this.expanded = this.defaultExpand ? 0 : [];
    }
  },
  mounted() {
    this.startDate = parseInt(this.from, 10) || null;
    this.endDate = parseInt(this.to, 10) || null;
    this.expanded = this.defaultExpand ? 0 : [];
    this.init();
  },
  methods: {
    isValidQuickRange(range, soFar) {
      const times = getTimeArrayFromRangeKey(range, soFar);
      return this.allowedDates(times[0] * 1000) && this.allowedDates(times[1] * 1000);
    },
    getExpandIcon(attr) {
      return attr === 'true' ? 'fal fa-angle-up' : 'fal fa-angle-down';
    },
    init() {
      moment.locale(this.$store.state.app.language || window.navigator.userLanguage || window.navigator.language);
      if (this.startDate === null || this.endDate === null) {
        const times = getTimeArrayFromRangeKey(this.initialRange);
        this.startDate = this.startDate || (this.allowNull ? null : times[0]);
        this.endDate = this.endDate || (this.allowNull ? null : times[1]);
      }
    },
    getIsoDateStringFromUnix(unixTimestamp) {
      return moment(unixTimestamp * 1000).format('YYYY-MM-DD');
    },
    getUnixFromIsoDate(isoDateString) {
      return moment(new Date(isoDateString)).unix();
    },
    getUnixFromLocaleDate(dateLocaleString) {
      return moment(dateLocaleString, this.dateOnly ? 'll' : 'lll', this.getLocale()).unix();
    },
    getLocale() {
      return this.$store.state.app.language || window.navigator.userLanguage || window.navigator.language;
    },
    getLocaleDateFromUnix(unixTimestamp) {
      if (unixTimestamp === null) {
        return '';
      }
      return moment(unixTimestamp * 1000).locale(this.getLocale()).format(this.dateOnly ? 'll' : 'lll');
    },
    handleQuickRangeClick(rangeKey, soFar) {
      const times = getTimeArrayFromRangeKey(rangeKey, soFar);
      this.startDate = times[0];
      this.endDate = times[1];
      this.handleApplyClick();
    },
    handleApplyClick() {
      if (this.validateInputDate(this.startDate) && this.validateInputDate(this.endDate) && this.validateInputRange(this.startDate, this.endDate)) {
        this.$emit('date:picked', {
          from: this.startDate,
          to: this.isRange ? this.endDate : undefined
        });
        this.expanded = [];
        this.hideAlternateText = true;
      }
    },
    handleStartDateChange(value) {
      const unixTimestamp = moment(value, this.dateOnly ? 'll' : 'lll', this.getLocale()).unix();
      if (!isNaN(unixTimestamp)) {
        this.startDate = unixTimestamp;
        this.$refs.formDatePicker.validate();
      }
    },
    handleEndDateChange(inputDateString) {
      const timestamp = this.getUnixFromLocaleDate(inputDateString);
      if (!isNaN(timestamp)) {
        this.endDate = timestamp;
        this.$refs.formDatePicker.validate();
      }
    },
    handleStartDatePicked(isoDate) {
      this.startDate = moment(isoDate).unix();
      this.$refs.formDatePicker.validate();
    },
    handleEndDatePicked(isoDate) {
      this.endDate = moment(isoDate).endOf('day').unix();
      this.$refs.formDatePicker.validate();
    },
    validateInputDate(inputDate) {
      const inputTS = isNaN(inputDate) ? this.getUnixFromLocaleDate(inputDate) : inputDate;
      return moment(inputTS * 1000).isValid();
    },
    validateInputRange(startDate = this.startDate, endDate = this.endDate) {
      // ignore rangecheck if component is not working with time ranges
      if (!this.isRange) {
        return true;
      }
      const startTS = isNaN(startDate) ? this.getUnixFromLocaleDate(startDate) : startDate;
      const endTS = isNaN(endDate) ? this.getUnixFromLocaleDate(endDate) : endDate;
      return moment(startTS * 1000).isBefore(endTS * 1000);
    }
  }
};